import React from 'react';
import { StockArticleDto } from '../../api';

/**
 * Compact inline visualisation of an article's price at publish + 10 forward-window
 * snapshots, rendered as a 11-column micro-bar-chart inside one SVG.
 *
 * Reading the chart:
 *   - Leftmost column (gray dot)      = priceAtPublish (the 0% reference)
 *   - Columns 1..10                   = +1h, +1d, +1w, +2w, +4w, +2m, +3m, +4m, +5m, +6m
 *   - Faint dotted line across middle = the priceAtPublish baseline
 *   - Bar above the line              = price went UP from publish (green)
 *   - Bar below the line              = price went DOWN from publish (red)
 *   - Bar colour intensity            = magnitude of % change, capped at ±15%
 *   - Missing snapshot                = nothing rendered in that slot (visible gap)
 *
 * Hover any bar to see "{label}: $X.XX ({+/-Y.Y%})" via the SVG <title> element.
 *
 * Empty-state rendering: the publisher-metrics backfill is async (51k+ rows,
 * Polygon-rate-limited) so many cards arrive with priceAtPublish=null. Rather
 * than leave the SVG visually empty — which reads as "broken chart" — we draw
 * an "awaiting backfill" caption next to the reference line so the user knows
 * the data is pending, not missing. The same caption appears when basis exists
 * but every forward window is still null (e.g. a 1-hour-old article where no
 * window has elapsed yet).
 */
export interface PriceSnapshotMiniChartProps {
    article: StockArticleDto;
    /** Total width in px, default 224. Height is fixed at 40. */
    width?: number;
}

interface Point {
    label: string;
    value: number | undefined;
}

function forwardPoints(a: StockArticleDto): Point[] {
    return [
        { label: '+1h', value: a.price1h },
        { label: '+1d', value: a.price1d },
        { label: '+1w', value: a.price1w },
        { label: '+2w', value: a.price2w },
        { label: '+4w', value: a.price4w },
        { label: '+2m', value: a.price2m },
        { label: '+3m', value: a.price3m },
        { label: '+4m', value: a.price4m },
        { label: '+5m', value: a.price5m },
        { label: '+6m', value: a.price6m },
    ];
}

const HEIGHT = 40;
const PAD_X  = 8;
const CENTER_Y = HEIGHT / 2;
/** % change at which colour saturation/bar height saturates. */
const PCT_CAP = 15;
/** Max bar extent above/below the reference line, in px. */
const MAX_BAR = (HEIGHT / 2) - 4;

const GREEN = [34, 197, 94];   // #22c55e
const RED   = [239, 68, 68];   // #ef4444
/** Caption colour — matches the dashed reference line + N/A pills in PriceSnapshotRow. */
const PLACEHOLDER_COLOR = '#9ca3af';
const PLACEHOLDER_TEXT = 'awaiting backfill';

function fillForPct(pct: number): string {
    const [r, g, b] = pct >= 0 ? GREEN : RED;
    const mag = Math.min(Math.abs(pct) / PCT_CAP, 1);
    // Floor opacity at 0.30 so even tiny moves are perceptible.
    const opacity = 0.30 + 0.70 * mag;
    return `rgba(${r},${g},${b},${opacity.toFixed(2)})`;
}

export const PriceSnapshotMiniChart: React.FC<PriceSnapshotMiniChartProps> = ({ article, width = 224 }) => {
    const basis = article.priceAtPublish;
    const hasBasis = typeof basis === 'number' && Number.isFinite(basis) && basis > 0;
    const forwards = forwardPoints(article);

    // 11 column x-positions: priceAtPublish first, then the 10 forwards. Evenly spaced.
    const usable = width - 2 * PAD_X;
    const step   = usable / 10;
    const colX   = (i: number) => PAD_X + i * step;

    const refY = CENTER_Y;
    const bars: React.ReactElement[] = [];

    // Column 0 — priceAtPublish: small neutral dot at the reference line.
    if (hasBasis) {
        bars.push(
            <circle
                key="basis"
                cx={colX(0)}
                cy={refY}
                r={3}
                fill="#6b7280"
            >
                <title>{`publish: $${basis!.toFixed(2)} (0%)`}</title>
            </circle>
        );
    }

    // Columns 1..10 — forward-window bars rooted at the reference line.
    let anyForwardRendered = false;
    forwards.forEach((pt, idx) => {
        const x = colX(idx + 1);
        if (!hasBasis) return;
        if (typeof pt.value !== 'number' || !Number.isFinite(pt.value)) return;
        const pct = (pt.value - basis!) / basis! * 100;
        const mag = Math.min(Math.abs(pct), PCT_CAP);
        const h   = (mag / PCT_CAP) * MAX_BAR;
        const y   = pct >= 0 ? refY - h : refY;
        anyForwardRendered = true;
        bars.push(
            <rect
                key={pt.label}
                x={x - 2.5}
                y={y}
                width={5}
                height={Math.max(h, 1)}
                fill={fillForPct(pct)}
                rx={1}
            >
                <title>{`${pt.label}: $${pt.value.toFixed(2)} (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`}</title>
            </rect>
        );
    });

    // Placeholder caption when there's nothing meaningful to draw. Two cases:
    //   - !hasBasis: priceAtPublish missing entirely → caption centred in the SVG.
    //   - hasBasis && !anyForwardRendered: dot is there but every forward window
    //     is null (e.g. very new article OR backfill hasn't filled forwards yet)
    //     → caption sits to the right of the dot.
    const showPlaceholder = !hasBasis || !anyForwardRendered;
    const placeholderX = !hasBasis
        ? width / 2
        : colX(0) + 8; // ~5px past the r=3 dot
    const placeholderAnchor: 'middle' | 'start' = !hasBasis ? 'middle' : 'start';

    return (
        <svg
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            style={{ display: 'block' }}
            role="img"
            aria-label={
                showPlaceholder
                    ? 'Forward price snapshots awaiting backfill'
                    : 'Forward price snapshots relative to publish price'
            }
        >
            {/* Faint dotted reference line at priceAtPublish (0% baseline). */}
            <line
                x1={PAD_X - 4}
                y1={refY}
                x2={width - PAD_X + 4}
                y2={refY}
                stroke="#9ca3af"
                strokeWidth={0.6}
                strokeDasharray="2 3"
                opacity={0.45}
            />
            {bars}
            {showPlaceholder && (
                <text
                    data-testid="price-snapshot-placeholder"
                    x={placeholderX}
                    y={refY}
                    textAnchor={placeholderAnchor}
                    dominantBaseline="middle"
                    fontSize={11}
                    fontFamily="monospace"
                    fill={PLACEHOLDER_COLOR}
                >
                    {PLACEHOLDER_TEXT}
                </text>
            )}
        </svg>
    );
};
